<template>
  <div class="grid gap-4">
    <PinInputRoot
      v-model="model"
      type="number"
      :disabled="disabled"
      class="flex justify-center gap-2"
      mask
      otp
    >
      <PinInputInput
        v-for="index in maxLength"
        :key="index"
        :index="index - 1"
        inputmode="none"
        ref="pinInputs"
        class="input h-14 w-12 text-center text-2xl"
        :class="{ 'input-primary': index <= model.length }"
        :autofocus="autofocus ? 'autofocus' : undefined"
        @keydown.enter.prevent="submit"
      />
    </PinInputRoot>

    <div role="alert" v-if="error" class="alert alert-error">
      <FontAwesomeIcon :icon="faCircleExclamation" />
      <p>{{ $t(`error.${error}`) }}</p>
    </div>

    <PinPad :disabled="disabled" @digit="appendDigit" @backspace="backspace" @clear="reset" />
  </div>
</template>

<script setup lang="ts">
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useIdle } from '@vueuse/core';
import { PinInputInput, PinInputRoot } from 'reka-ui';
import { onMounted, useTemplateRef, watch, type ComponentInstance } from 'vue';
import { PIN_ENTRY_RESET_TIMEOUT, PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '../../constants';
import PinPad from './PinPad.vue';

const props = withDefaults(
  defineProps<{
    minLength?: number;
    maxLength?: number;
    disabled?: boolean;
    autofocus?: boolean;
    /** Error translation key rendered below the cells (looked up under `error.*`). */
    error?: string | null;
    /** Inactivity after which entered digits are wiped. Set to `0` to keep them. */
    resetAfter?: number;
  }>(),
  {
    minLength: PIN_MIN_LENGTH,
    maxLength: PIN_MAX_LENGTH,
    disabled: false,
    error: null,
    resetAfter: PIN_ENTRY_RESET_TIMEOUT,
  },
);

/** The digits entered so far. */
const model = defineModel<number[]>({ default: () => [] });

const emit = defineEmits<{ (e: 'complete'): void }>();

const pinInputs = useTemplateRef<ComponentInstance<HTMLInputElement>[]>('pinInputs');

const appendDigit = (digit: string) => {
  if (model.value.length >= props.maxLength) return;

  model.value = [...model.value, Number(digit)];
};

const backspace = () => (model.value = model.value.slice(0, -1));

const focus = () => pinInputs.value?.[0]?.$el.focus();

const reset = () => {
  model.value = [];
  focus();
};

const hasFocus = () => !!pinInputs.value?.some((input) => input.$el === document.activeElement);

// Someone who walks away mid-entry must not leave their digits on the pad for
// whoever comes next, so entries drop out once the device goes untouched.
const { idle } = useIdle(props.resetAfter);

watch(idle, (isIdle) => {
  if (!isIdle || !props.resetAfter || !model.value.length) return;
  // A submit is still in flight - its own error path clears the pad.
  if (props.disabled) return;

  // Only pull focus back to the first cell if the pad holds it anyway, so a
  // pad sitting next to other fields does not steal the caret from them.
  if (hasFocus()) reset();
  else model.value = [];
});

const submit = () => {
  if (model.value.length < props.minLength) return;
  emit('complete');
};

// Not the cells' own `complete` event: that fires as soon as as many cells are
// filled as have registered, which an empty pad satisfies too.
watch(model, (digits) => {
  if (digits.length >= props.maxLength) emit('complete');
});

defineExpose({ reset, focus });

onMounted(focus);
</script>
